// OMNI Design System Card Recipe for Chakra UI v3
// Based on OMNI CSS card and container styles

import { defineSlotRecipe } from '@chakra-ui/react';

export const cardRecipe = defineSlotRecipe({
  className: 'omni-card',
  slots: ['root', 'header', 'title', 'subtitle', 'body', 'footer'],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      backgroundColor: 'bg.surface',
      border: '1px solid',
      borderColor: 'border.default',
      borderRadius: 'base',
      boxShadow: 'card',
      overflow: 'hidden',
      transition: 'all 200ms ease',

      _hover: {
        boxShadow: 'container-level-two',
        borderColor: 'border.emphasis',
      },

      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '2px',
      },
    },

    header: {
      display: 'flex',
      flexDirection: 'column',
      padding: '4',
      borderBottom: '1px solid',
      borderBottomColor: 'border.subtle',
      backgroundColor: 'bg.subtle',

      // Header without border (when no body/footer)
      _last: {
        borderBottomWidth: 0,
      },
    },

    title: {
      fontSize: 'lg',
      fontWeight: 'semibold',
      color: 'fg.default',
      lineHeight: 'shorter',
      marginBottom: '1',

      _last: {
        marginBottom: '0',
      },
    },

    subtitle: {
      fontSize: 'sm',
      fontWeight: 'normal',
      color: 'fg.muted',
      lineHeight: 'short',
    },

    body: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: '4',
      color: 'fg.default',
      fontSize: 'md',
      lineHeight: 'base',

      // Body without header
      _first: {
        paddingTop: '4',
      },

      // Body without footer
      _last: {
        paddingBottom: '4',
      },
    },

    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '4',
      borderTop: '1px solid',
      borderTopColor: 'border.subtle',
      backgroundColor: 'bg.subtle',
      gap: '3',

      // Footer without border (when no body)
      _first: {
        borderTopWidth: 0,
      },
    },
  },

  variants: {
    size: {
      xs: {
        header: {
          padding: '2',
        },
        title: {
          fontSize: 'sm',
        },
        subtitle: {
          fontSize: '2xs',
        },
        body: {
          padding: '2',
          fontSize: 'xs',
        },
        footer: {
          padding: '2',
          gap: '2',
        },
      },

      sm: {
        header: {
          padding: '3',
        },
        title: {
          fontSize: 'md',
        },
        subtitle: {
          fontSize: 'xs',
        },
        body: {
          padding: '3',
          fontSize: 'sm',
        },
        footer: {
          padding: '3',
          gap: '2',
        },
      },

      md: {
        header: {
          padding: '4',
        },
        title: {
          fontSize: 'lg',
        },
        subtitle: {
          fontSize: 'sm',
        },
        body: {
          padding: '4',
          fontSize: 'md',
        },
        footer: {
          padding: '4',
          gap: '3',
        },
      },

      lg: {
        header: {
          padding: '5',
        },
        title: {
          fontSize: 'xl',
        },
        subtitle: {
          fontSize: 'md',
        },
        body: {
          padding: '5',
          fontSize: 'md',
        },
        footer: {
          padding: '5',
          gap: '3',
        },
      },

      xl: {
        header: {
          padding: '6',
        },
        title: {
          fontSize: '2xl',
        },
        subtitle: {
          fontSize: 'lg',
        },
        body: {
          padding: '6',
          fontSize: 'lg',
        },
        footer: {
          padding: '6',
          gap: '4',
        },
      },
    },

    variant: {
      outline: {
        root: {
          border: '1px solid',
          borderColor: 'border.default',
          backgroundColor: 'bg.surface',
        },
      },

      elevated: {
        root: {
          border: 'none',
          boxShadow: 'container-level-two',
          backgroundColor: 'bg.surface',

          _hover: {
            boxShadow: 'container-level-three',
          },
        },
      },

      filled: {
        root: {
          border: 'none',
          backgroundColor: 'bg.subtle',
          boxShadow: 'none',

          _hover: {
            backgroundColor: 'bg.muted',
            boxShadow: 'card',
          },
        },
        header: {
          backgroundColor: 'bg.muted',
        },
        footer: {
          backgroundColor: 'bg.muted',
        },
      },

      ghost: {
        root: {
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',

          _hover: {
            backgroundColor: 'bg.subtle',
            boxShadow: 'none',
          },
        },
        header: {
          backgroundColor: 'transparent',
        },
        footer: {
          backgroundColor: 'transparent',
        },
      },

      unstyled: {
        root: {
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          borderRadius: 'none',

          _hover: {
            boxShadow: 'none',
            borderColor: 'transparent',
          },
        },
        header: {
          padding: '0',
          border: 'none',
          backgroundColor: 'transparent',
        },
        body: {
          padding: '0',
        },
        footer: {
          padding: '0',
          border: 'none',
          backgroundColor: 'transparent',
        },
      },
    },

    colorScheme: {
      brand: {
        root: {
          borderColor: 'brand.200',

          _hover: {
            borderColor: 'brand.300',
            boxShadow: '0 4px 12px rgba(0, 118, 255, 0.15)',
          },
        },
        header: {
          backgroundColor: 'brand.50',
          borderBottomColor: 'brand.100',
        },
        footer: {
          backgroundColor: 'brand.50',
          borderTopColor: 'brand.100',
        },
      },

      gray: {
        root: {
          borderColor: 'gray.200',

          _hover: {
            borderColor: 'gray.300',
          },
        },
        header: {
          backgroundColor: 'gray.50',
          borderBottomColor: 'gray.100',
        },
        footer: {
          backgroundColor: 'gray.50',
          borderTopColor: 'gray.100',
        },
      },

      success: {
        root: {
          borderColor: 'green.200',

          _hover: {
            borderColor: 'green.300',
            boxShadow: '0 4px 12px rgba(44, 196, 173, 0.15)',
          },
        },
        header: {
          backgroundColor: 'green.50',
          borderBottomColor: 'green.100',
        },
        footer: {
          backgroundColor: 'green.50',
          borderTopColor: 'green.100',
        },
      },

      warning: {
        root: {
          borderColor: 'yellow.200',

          _hover: {
            borderColor: 'yellow.300',
            boxShadow: '0 4px 12px rgba(254, 216, 130, 0.15)',
          },
        },
        header: {
          backgroundColor: 'yellow.50',
          borderBottomColor: 'yellow.100',
        },
        footer: {
          backgroundColor: 'yellow.50',
          borderTopColor: 'yellow.100',
        },
      },

      error: {
        root: {
          borderColor: 'red.200',

          _hover: {
            borderColor: 'red.300',
            boxShadow: '0 4px 12px rgba(223, 86, 120, 0.15)',
          },
        },
        header: {
          backgroundColor: 'red.50',
          borderBottomColor: 'red.100',
        },
        footer: {
          backgroundColor: 'red.50',
          borderTopColor: 'red.100',
        },
      },
    },

    interactive: {
      true: {
        root: {
          cursor: 'pointer',
          transition: 'all 200ms ease',

          _hover: {
            transform: 'translateY(-2px)',
            boxShadow: 'container-level-three',
          },

          _active: {
            transform: 'translateY(0)',
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      variant: 'elevated',
      interactive: true,
      css: {
        root: {
          _hover: {
            transform: 'translateY(-4px)',
            boxShadow: 'container-level-four',
          },
        },
      },
    },

    {
      variant: 'filled',
      colorScheme: 'brand',
      css: {
        root: {
          backgroundColor: 'brand.25',

          _hover: {
            backgroundColor: 'brand.50',
          },
        },
        header: {
          backgroundColor: 'brand.50',
        },
        footer: {
          backgroundColor: 'brand.50',
        },
      },
    },

    {
      variant: 'filled',
      colorScheme: 'gray',
      css: {
        root: {
          backgroundColor: 'gray.25',

          _hover: {
            backgroundColor: 'gray.50',
          },
        },
        header: {
          backgroundColor: 'gray.50',
        },
        footer: {
          backgroundColor: 'gray.50',
        },
      },
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'outline',
    colorScheme: 'gray',
  },
});
